import { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { user } from "@lhd/features/users/selector";
import dayjs from "dayjs";
import {
  useFetchUnreadThreadsInfinite,
  useMarkThreadAsRead,
  useDebounce,
  useFetchChatStatuses,
} from "@lhd/hooks";
import { ListMessItem } from "@lhd/types";

type ChatThreadDisplay = {
  id: string;
  subject: string;
  requester: string;
  companyName: string;
  status: string | null;
  statusId: string | number | undefined;
  createdAt: string;
  isUpdated: boolean;
  unreadCount: number;
  company_id?: string | number | null;
};

interface UseUnreadThreadsListProps {
  isOpen: boolean;
  onRefetchReady?: (refetch: () => void) => void;
}

export const useUnreadThreadsList = ({
  isOpen,
  onRefetchReady,
}: UseUnreadThreadsListProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const auth = useSelector(user);
  const isAdmin =
    !!auth?.is_admin &&
    (!!auth?.workers?.some((worker) => worker?.company?.is_admin) ||
      !!auth?.is_root_company);

  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const { markAsRead } = useMarkThreadAsRead();
  const { data: statusesData } = useFetchChatStatuses({ enabled: isOpen });

  const statusOptions = useMemo(() => {
    return (statusesData || []).map((s) => ({
      label: s.name,
      value: String(s.id),
      color: s.color,
    }));
  }, [statusesData]);

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
    },
    500,
    [searchValue],
  );

  const {
    data: unreadThreads,
    loading: threadsLoading,
    loadingMore,
    hasMore,
    loadMore,
    reset,
    refetch,
  } = useFetchUnreadThreadsInfinite({
    enabled: isOpen,
    pageSize: 15,
    search: debouncedSearchValue,
  });

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  // Expose refetch function to parent
  useEffect(() => {
    if (onRefetchReady && refetch) {
      onRefetchReady(refetch);
    }
  }, [onRefetchReady, refetch]);

  const observerTargetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) {
          loadMore();
        }
      },
      {
        threshold: 0.1,
        root: itemsContainerRef.current,
        rootMargin: "0px 0px 100px 0px",
      },
    );

    const currentTarget = observerTargetRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
      observer.disconnect();
    };
  }, [hasMore, isOpen, loadMore, loadingMore]);

  // Map API threads to display format with update tracking
  const chatThreads = useMemo(() => {
    if (!unreadThreads || unreadThreads.length === 0) {
      return [];
    }

    return unreadThreads.map((item: ListMessItem & { _updated?: boolean }) => {
      const threadIdStr = item.id?.replace("#", "") || "";
      const createdAt = item.updatedDate
        ? dayjs(item.updatedDate).utcOffset(9).format("YYYY/MM/DD HH:mm")
        : "";

      return {
        id: threadIdStr,
        subject: item.subject || "-",
        requester: item.creator || "-",
        companyName: item.customerCompany ? `(${item.customerCompany})` : "",
        status: item.status || null,
        statusId: item.status_id ?? undefined,
        createdAt,
        isUpdated: item._updated || false,
        unreadCount: item.unreadCount || 0,
        company_id: item.company_id ?? undefined,
      } as ChatThreadDisplay;
    });
  }, [unreadThreads]);

  return {
    searchValue,
    setSearchValue,
    chatThreads,
    threadsLoading,
    loadingMore,
    itemsContainerRef,
    observerTargetRef,
    isAdmin,
    auth,
    markAsRead,
    statusOptions,
    refetch,
  };
};

export type { ChatThreadDisplay };
